import { Injectable, Injector } from '@angular/core';
import { Subject, BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { Cluster } from './cluster.interface';
import { BuildingService } from '../building/building.service';
import { first, filter } from 'rxjs/operators';
import { Area } from '../area/area.interface';
import { Building } from '../building/building.interface';
import { ApiService } from '../../api/api.service';
import { ProjectService } from '../project/project.service';
import { Project } from '../project/project.interface';
import { AreaService } from '../area/area.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Cluster core service.
 *
 * Contains all the private logic for Cluster management.
 *
 * **Only ClusterService is allowed to access this Service.**
 */
@Injectable({
  providedIn: 'root'
})
export class ClusterCoreService {

  /** Area service reference for the injector. */
  private areaService: AreaService;

  /** Cluster subject. */
  protected _cluster: Subject<Cluster[]> = new BehaviorSubject<Cluster[]>(null);

  /** Cluster observable. */
  public cluster$ = this._cluster.asObservable();

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected buildingService: BuildingService,
    protected injector: Injector,
    protected projectService: ProjectService
  ) {
    this._trackBuildings();
  }

  /**
   * Get clusters.
   *
   * @returns The clusters in the current project.
   */
  public get(): Cluster[] {
    let clusters: Cluster[];
    this.cluster$.pipe(first()).subscribe(c => clusters = c);
    return clusters;
  }

  /**
   * Get specific cluster.
   *
   * @param id The id of the cluster.
   * @returns The requested cluster.
   */
  public getCluster(id: Cluster['_id']): Cluster {
    return this.get().find(c => c._id === id);
  }

  /**
   * Get parent cluster for an area.
   *
   * @param areaId Child area id.
   * @returns The parent cluster of the area.
   */
  public getClusterByAreaId(areaId: Area['_id']): Cluster {
    let cluster: Cluster;
    this.get().forEach(cl => {
      const match = cl.areas.find(a => a._id === areaId);
      if (match) {
        cluster = cl;
        return;
      }
    });
    return cluster;
  }

  /**
   * Get clusters for specific building.
   *
   * @param buildingId The parent building id.
   * @returns Clusters within the building.
   */
  public getClusterByBuildingId(buildingId: Building['_id']): Cluster[] {
    return JSON.parse(JSON.stringify(this.get().filter(c => c.buildingId === buildingId)));
  }

  /**
   * Add new cluster.
   *
   * @param building The parent building.
   * @param template The cluster template.
   * @returns The new cluster.
   */
  protected _add(building: Building, template: Partial<Cluster>): Observable<Cluster> {
    return new Observable(observer => {
      this.apiService.addCluster(building, {
        ...template,
        ratio: this._getDefaultClusterRatio(building)
      }).subscribe(response => {
        const cluster = {
          ...response,
          buildingId: building._id
        };
        this.projectService.addCluster(cluster);
        observer.next(cluster);
        observer.complete();
      });
    });
  }

  /**
   * Delete cluster from project.
   *
   * When deleting a cluster, the areas should be deactivated too, but only have to locally.
   * Back-end this is handled by the API.
   *
   * @note Make sure the areas are deleted *before* the cluster, otherwise their location
   * cannot be reached anymore.
   * - project > building > cluster (deleted) > area.
   *
   * @param cluster The cluster to delete.
   * @returns Whether the delete is successful.
   */
  protected _delete(cluster: Cluster): Observable<boolean> {
    return new Observable(observer => {
      this.apiService.deleteCluster(cluster).subscribe(results => {

        // If the cluster contains areas, make sure these (and their requirements) are
        // deactivated as well. If there are no areas, just deactive the cluster as is.
        if (cluster.areas.length) {
          this.areaService = this.areaService || this.injector.get('AreaService');
          const area$ = [];
          for (const area of cluster.areas) {
            area$.push(this.areaService.delete(area, false, { local: true }));
          }
          combineLatest(area$).subscribe(() => {
            this.projectService.patchCluster(cluster, { active: false });
          });
        } else {
          this.projectService.patchCluster(cluster, { active: false });
        }

        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * Default ratio property when creating a new cluster.
   *
   * When a new cluster is created, the `ratio` should be determined based on
   * the user settings, which is a combination of:
   * - Ratio by: Building / Cluster
   * - Ratio as: Ratio / BVO
   *
   * There can be 4 possible combination.
   *
   * @param building The parent building.
   * @returns The cluster ratio based on the current user settings.
   */
  private _getDefaultClusterRatio(building: Building): Cluster['ratio'] {
    switch (true) {
      case (building.ratioBy === 'building' && building.ratioAs === 'ratio'):
        return building.ratio;
      case (building.ratioBy === 'building' && building.ratioAs === 'bvo'):
        return building.ratio;
      case (building.ratioBy === 'cluster' && building.ratioAs === 'ratio'):
        return 1;
      case (building.ratioBy === 'cluster' && building.ratioAs === 'bvo'):
        return 1;
    }
  }

  /**
   * Patch cluster.
   *
   * @param cluster The cluster to patch.
   * @param values The new values.
   * @param [config] Optional config object.
   * @returns Observable as boolean, when the patch succeeded.
   */
  protected _patch(cluster: Cluster, values: Partial<Cluster>, config?: any): Observable<boolean> {
    return new Observable(observer => {
      this.apiService.updateCluster(cluster, values).subscribe(response => {
        this.projectService.patchCluster(cluster, response);
        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * Track buildings and set clusters.
   */
  protected _trackBuildings(): void {
    this.buildingService.building$.pipe(
      filter(buildings => buildings !== null)
    ).subscribe(buildings => {
      const clusters = [];
      buildings.forEach(building => {
        for (const cluster of building.clusters) {
          cluster.buildingId = building._id;
        }
        clusters.push(...building.clusters);
      });
      this._cluster.next(clusters);
    });
  }
}
